import { inject } from '@angular/core';
import {
  Router,
  type ActivatedRouteSnapshot,
  type CanActivateFn,
  type RouterStateSnapshot,
  type UrlTree,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { getParamsObject } from '@cosmos/core';
import { LocalStorageService } from '@cosmos/data-access-storage';
import { getParam } from '@cosmos/router';
import { injectWindow } from '@cosmos/util-common/di';
import { dispatchPdfDocumentError } from '@esp/common/util-pdf-generation';

import { CustomerPortalCollectionAuthActions } from '../actions';
import { CustomerPortalCollectionAuthQueries } from '../queries';
import { getCustomerPortalProductAuthDataFromLocalStorage } from '../utils/local-storage-auth-data';

export function customerPortalProductAccessGuard(): CanActivateFn {
  return async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<UrlTree | boolean> => {
    const _store = inject(Store);
    const _router = inject(Router);
    const _lsService = inject(LocalStorageService);
    const _window = injectWindow();
    // Print routes (print or print/pdf) are also rendered by the PDF worker.
    const isPrintRoute = state.url.includes('/print');
    const params = _getParams(route);

    const session = _store.selectSnapshot(
      CustomerPortalCollectionAuthQueries.getSession
    );

    if (session && !params?.accessCode) {
      return true;
    }

    if (params?.accessCode && params?.productId) {
      try {
        await firstValueFrom(
          _store.dispatch(
            new CustomerPortalCollectionAuthActions.LoginWithAccessCode({
              accessCode: params!.accessCode,
              productId: params!.productId,
            })
          ),
          { defaultValue: undefined }
        );
      } catch (error) {
        // The page won't render — abort the PDF worker instead of letting it
        // wait for the ready selector until it times out.
        if (isPrintRoute) dispatchPdfDocumentError(_window, error, 401);
        throw error;
      }

      return _getUrlTreeWithoutAccessCode(_router, state);
    }

    if (!session) {
      const productId = getParam(route, 'productId');
      if (productId) {
        const authData = getCustomerPortalProductAuthDataFromLocalStorage(
          _lsService,
          productId
        );
        if (authData) {
          await firstValueFrom(
            _store.dispatch(
              new CustomerPortalCollectionAuthActions.RestoreFromLocalStorage(
                authData.session,
                authData.accessCode
              )
            ),
            { defaultValue: undefined }
          );
          return true;
        }
      }
    }

    if (isPrintRoute) dispatchPdfDocumentError(_window, null, 404);
    return _router.createUrlTree(['not-found']);
  };
}

function _getParams(route: ActivatedRouteSnapshot) {
  const params = getParamsObject(route.paramMap, {
    optional: ['productId'],
  });

  const queryParams = getParamsObject(route.queryParamMap, {
    optional: ['accessCode'],
  });

  return (
    params &&
    queryParams && {
      productId: params.productId && parseInt(params.productId, 10),
      accessCode: queryParams?.accessCode,
    }
  );
}

function _getUrlTreeWithoutAccessCode(
  router: Router,
  state: RouterStateSnapshot
): UrlTree {
  const urlTree = router.parseUrl(state.url);
  delete urlTree.queryParams['accessCode'];
  return urlTree;
}
